import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Globe, Mail } from "lucide-react";
import AdminPageBackground from "@/components/admin/AdminPageBackground";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { useState } from "react";

const NewsletterAdmin = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const { toast } = useToast();
  
  const [emailConfig, setEmailConfig] = useState({
    email: "",
    password: "",
    host: "smtp.gmail.com",
    port: "587",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    toast({
      title: isRTL ? "تم حفظ الإعدادات" : "Settings Saved",
      description: isRTL 
        ? "تم حفظ إعدادات البريد الإلكتروني بنجاح"
        : "Email settings have been saved successfully",
      duration: 3000,
    });
  };

  return (
    <AdminPageBackground className={isRTL ? "rtl" : "ltr"}>
      <nav className="border-b border-white/10 bg-white/5 backdrop-blur-sm">
        <div className="max-w-[2000px] mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link
              to="/admin"
              className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              {isRTL ? "العودة للوحة التحكم" : "Back to Dashboard"}
            </Link>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => i18n.changeLanguage(i18n.language === "en" ? "ar" : "en")}
              className="rounded-full text-white/70 hover:text-white hover:bg-white/10"
            >
              <Globe className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </nav>

      <main className="max-w-[2000px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-2 mb-8">
          <Mail className="w-6 h-6 text-white" />
          <h1 className="text-3xl font-bold text-white">
            {isRTL ? "إعدادات النشرة الإخبارية" : "Newsletter Settings"}
          </h1>
        </div>

        <div className="bg-white/10 backdrop-blur-md rounded-lg border border-white/15 p-6">
          <form onSubmit={handleSubmit} className="space-y-6 max-w-xl">
            <div className="space-y-2">
              <Label className="text-white/70">
                {isRTL ? "البريد الإلكتروني" : "Email Address"}
              </Label>
              <Input
                type="email"
                value={emailConfig.email}
                onChange={(e) => setEmailConfig({...emailConfig, email: e.target.value})}
                placeholder={isRTL ? "أدخل بريدك الإلكتروني" : "Enter your email"}
                className="bg-white/10 border-white/15 text-white placeholder:text-white/40"
                required
              />
            </div>

            <div className="space-y-2">
              <Label className="text-white/70">
                {isRTL ? "كلمة مرور التطبيق" : "App Password"}
              </Label>
              <Input
                type="password"
                value={emailConfig.password}
                onChange={(e) => setEmailConfig({...emailConfig, password: e.target.value})}
                placeholder={isRTL ? "أدخل كلمة مرور التطبيق" : "Enter app password"}
                className="bg-white/10 border-white/15 text-white placeholder:text-white/40"
                required
              />
              <p className="text-sm text-white/40">
                {isRTL 
                  ? "ملاحظة: استخدم كلمة مرور التطبيق من إعدادات Gmail للأمان"
                  : "Note: Use an App Password from Gmail settings for security"
                }
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-white/70">
                {isRTL ? "خادم SMTP" : "SMTP Host"}
              </Label>
              <Input
                type="text"
                value={emailConfig.host}
                onChange={(e) => setEmailConfig({...emailConfig, host: e.target.value})}
                placeholder="smtp.gmail.com"
                className="bg-white/10 border-white/15 text-white placeholder:text-white/40"
                required
              />
            </div>

            <div className="space-y-2">
              <Label className="text-white/70">
                {isRTL ? "منفذ SMTP" : "SMTP Port"}
              </Label>
              <Input
                type="text"
                value={emailConfig.port}
                onChange={(e) => setEmailConfig({...emailConfig, port: e.target.value})}
                placeholder="587"
                className="bg-white/10 border-white/15 text-white placeholder:text-white/40"
                required
              />
            </div>

            <Button type="submit" className="w-full">
              {isRTL ? "حفظ الإعدادات" : "Save Settings"}
            </Button>
          </form>
        </div>

        <div className="mt-6 bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
          <h2 className="text-lg font-semibold text-blue-300 mb-2">
            {isRTL ? "تعليمات مهمة" : "Important Instructions"}
          </h2>
          <ul className="list-disc list-inside space-y-2 text-blue-200/80">
            <li>
              {isRTL 
                ? "قم بتمكين المصادقة الثنائية في حساب Gmail الخاص بك"
                : "Enable 2-Factor Authentication in your Gmail account"
              }
            </li>
            <li>
              {isRTL
                ? "قم بإنشاء كلمة مرور تطبيق من إعدادات الأمان في Gmail"
                : "Create an App Password from Gmail security settings"
              }
            </li>
            <li>
              {isRTL
                ? "استخدم كلمة مرور التطبيق بدلاً من كلمة المرور العادية"
                : "Use the App Password instead of your regular password"
              }
            </li>
          </ul>
        </div>
      </main>
    </AdminPageBackground>
  );
};

export default NewsletterAdmin;
